import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { CommonModule, Location } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '../button/button.component';
import { RouterModule, Router } from '@angular/router';
import { IconComponent } from '../icon/icon.component';

export interface IBreadcrumb {
  label: string;
  link?: string;
}

@Component({
  selector: 'ui-page-header',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, ButtonComponent, SvgIconComponent, IconComponent],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
})
export class PageHeaderComponent {
  @Input() title: string = '';
  @Input() titleTag: string = '';
  @Input() titleTagType: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'danger' = 'secondary';
  /** Legacy asset-based icon name (src/assets/icones). Prefer `lucideIcon`. */
  @Input() icon: string = '';
  /**
   * Lucide icon name, rendered at 26px — the same icon the sidebar uses for this
   * section, one step larger. Takes precedence over `icon` when both are set.
   */
  @Input() lucideIcon: string = '';
  @Input() showBackButton: boolean = false;
  @Input() breadcrumbs: IBreadcrumb[] = [];
  @Input() titleSize: 'large' | 'xlarge' = 'large';

  /**
   * Opt-in inline search, living in the header's right-hand block instead of a
   * separate action bar below it (WB-7275). The host keeps ownership of the
   * control and the query; the header only owns the chrome and the expand.
   *
   * On focus the field grows and the rest of the row steps back — projected
   * actions collapse and the title dims — so the page reads as "searching".
   * That has to happen inside this component because both of those elements
   * belong to it, which is also why the behaviour is reusable by Products and
   * Catalogs rather than re-implemented per page.
   */
  @Input() searchable = false;
  @Input() searchControl = new FormControl('');
  @Input() searchPlaceholder = '';
  /**
   * Forwarded onto the search input as `data-testid`. Unset by default, so no
   * existing usage changes. Kept apart from `id`, which is a real DOM id some
   * screens address directly.
   */
  @Input() searchTestId = '';
  @Output() searchInput = new EventEmitter<Event>();

  @ViewChild('headerRow', { static: true }) private headerRow?: ElementRef<HTMLElement>;

  /** Must match `$search-width-expanded` in the stylesheet. */
  private static readonly SEARCH_WIDTH_EXPANDED = 560;

  searchFocused = false;
  /** Inline translateX that puts the expanded field on the page's centre line. */
  searchShift = 0;

  onSearchFocus(): void {
    // The field grows leftwards from the right edge of the row, so once expanded
    // its centre sits at (rowWidth - width/2). Shifting it by the difference from
    // the row's own centre lands it dead centre. Never shift right: on a narrow
    // row the field already fills the width.
    const rowWidth = this.headerRow?.nativeElement.offsetWidth ?? 0;
    this.searchShift = Math.min(0, (PageHeaderComponent.SEARCH_WIDTH_EXPANDED - rowWidth) / 2);
    this.searchFocused = true;
  }

  constructor(
    private location: Location,
    private router: Router,
  ) {}

  onBackButtonClick() {
    if (
      this.breadcrumbs.length > 0 &&
      this.breadcrumbs[this.breadcrumbs.length - 1].link
    ) {
      this.router.navigateByUrl(this.breadcrumbs[this.breadcrumbs.length - 1].link!);
    } else {
      this.location.back();
    }
  }
}
